import { useCallback, useMemo, useState } from 'react';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { isEmpty, isNaN } from 'lodash/lang';
import moment from 'moment/moment';

export default function useDataGrid() {
  const [pinnedColumns, setPinnedColumns] = useState({ left: ['logRowNumber'] });
  const [raw, setRaw] = useState({ timestamp: null, rawRows: [], rawColumns: [] });

  const rows = raw.rawRows,
    rawColumns = raw.rawColumns,
    timestamp = raw.timestamp;

  const columns = useMemo(() => collectColumns(rawColumns), [rawColumns]);

  const setGridData = useCallback(function(raw) {
    setRaw(raw);
    setPinnedColumns(collectPinnedColumns(raw.rawColumns));
  }, []);

  const onPinnedColumnsChange = useCallback(updatedPinnedColumns => {
    setPinnedColumns(updatedPinnedColumns);
  }, []);

  return { timestamp, columns, rows, pinnedColumns, onPinnedColumnsChange, setGridData };
}

function collectPinnedColumns(rawColumns) {
  const pinnedKeys = rawColumns.filter(e => e.fixedField).map(({ key }) => key);
  return { left: ['logRowNumber', ...pinnedKeys] };
}

function collectColumns(rawColumns) {
  if (isEmpty(rawColumns)) {
    return [];
  }
  return [
    {
      field: 'logRowNumber',
      headerName: 'N',
      maxWidth: 50,
      resizable: false,
      disableColumnMenu: true
    },
    ...rawColumns.map(function(el) {
      const { key, question, investigatorField, inputType } = el;
      const column = {
        field: key,
        headerName: question,
        maxWidth: 240,
        sortComparator: defaultSortComparator,
        valueGetter: rawValueResolver
      };
      if (investigatorField) {
        column.headerName = (
          <>
            {investigatorField && <MedicalServicesIcon sx={{ marginTop: '-4px', marginRight: '5px' }} />}
            {question}
          </>
        );
      }
      if (inputType === 'date') {
        column.sortComparator = dateSortComparator;
      }
      return column;
    })
  ];
}

function defaultSortComparator(a, b) {
  const ansA = normalizeAnswer(a);
  const ansB = normalizeAnswer(b);

  if (ansA === ansB) {
    return 0;
  }
  return ansA > ansB ? -1 : 1;
}

function normalizeAnswer(v) {
  const pv = (v || '').toLowerCase();
  const nv = +pv;
  if (!isNaN(nv)) {
    return nv;
  }
  return pv;
}

function dateSortComparator(a, b) {
  let ansA = getDateString(a);
  let ansB = getDateString(b);

  if (ansA === ansB) {
    return 0;
  }
  return ansA > ansB ? -1 : 1;
}

function getDateString(v) {
  const vSplit = v?.split('/');
  const yearV = vSplit[2] || 'YYYY';
  const monthV = vSplit[1] || 'UNK';
  const dayV = vSplit[0] || 'UN';
  return `${yearV}/${monthV !== 'UNK' ? moment(monthV, 'MMM').format('MM') : monthV}/${dayV}`;
}

function rawValueResolver(value, row, column) {
  const cellData = row.data[column.field];
  if (cellData?.attributeType === 'file') {
    if (cellData.files?.length) {
      return 'Yes';
    }
    return '--';
  }
  if (cellData?.answer) {
    return cellData.answer;
  }
  return '--';
}
